import { useState } from "react";
import { describeVoters, hasLiked, likeCount, type ListenerDoc, type TrackDoc } from "radio-shared";
import { api } from "../api";

/**
 * Le cœur du son en cours.
 *
 * À l'inverse du skip, il ne décide de rien : ni la file ni l'antenne ne
 * bougent. C'est pourquoi il reste discret tant que personne n'a cliqué, et
 * pourquoi il n'affiche aucun seuil — il n'y en a pas à atteindre.
 *
 * Rien n'est tenu en local : le compte et les visages sortent du document du
 * son, que Firestore pousse à tout le monde. Le cœur d'un collègue s'allume donc
 * ici sans qu'on ait rien à demander.
 */
export function LikeButton({
  track,
  myUid,
  listeners,
}: {
  track: TrackDoc;
  myUid: string;
  listeners: ListenerDoc[];
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = likeCount(track.likes);
  const mine = hasLiked(track.likes, myUid);
  // mêmes visages que pour le skip : des uid croisés avec les présents, et
  // « quelqu'un » pour qui a aimé puis fermé son onglet
  const fans = describeVoters(track.likes, listeners);

  const toggle = async () => {
    setError(null);
    setPending(true);
    try {
      await api.like(track.id);
      // rien à écrire ici : le document du son revient par onSnapshot
    } catch (err) {
      setError(err instanceof Error ? err.message : "raté");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="like">
      <button
        className={`like__btn${mine ? " like__btn--mine" : ""}`}
        type="button"
        aria-pressed={mine}
        aria-label={mine ? "retirer mon cœur" : "aimer ce son"}
        disabled={pending}
        onClick={() => void toggle()}
        title={
          mine
            ? "tu aimes ce son — reclique pour retirer ton cœur"
            : count > 0
              ? `${fans.map((f) => f.name).join(", ")} aime${fans.length > 1 ? "nt" : ""} ce son`
              : "dire que ce son te plaît"
        }
      >
        <span className="like__heart" aria-hidden>
          {mine ? "♥" : "♡"}
        </span>
        {count > 0 && count}
      </button>

      {fans.length > 0 && (
        <div className="like__faces" aria-hidden>
          {fans.slice(0, 5).map((f) =>
            f.photo ? (
              <img key={f.uid} className="avatar avatar--sm" src={f.photo} alt="" />
            ) : (
              <span key={f.uid} className="avatar avatar--sm avatar--initial">
                {f.name.slice(0, 1).toUpperCase()}
              </span>
            ),
          )}
        </div>
      )}

      {error && <span className="like__error">{error}</span>}
    </div>
  );
}
